import { CalendarDays, CircleCheck, Mail, MessageSquare, Phone, type LucideIcon } from 'lucide-react';
import moment from 'moment';
import { cn } from '@/lib/utils';
import type { SequenceAction } from '@/slices/remindersApi';
import type { Enrollment } from '@/slices/reminderSequencesApi';

// One icon, label and tint per action, used everywhere a step appears.

export interface ActionMeta {
	label: string;
	/** The title a new step starts with */
	defaultTitle: string;
	icon: LucideIcon;
	/** Icon colour */
	tint: string;
	/** Soft background behind the icon */
	wash: string;
}

export const SEQUENCE_ACTIONS: SequenceAction[] = ['call', 'text', 'email', 'meeting', 'todo'];

export const ACTION_META: Record<SequenceAction, ActionMeta> = {
	call: {
		label: 'Call',
		defaultTitle: 'Call',
		icon: Phone,
		tint: 'text-emerald-600 dark:text-emerald-400',
		wash: 'bg-emerald-500/10',
	},
	text: {
		label: 'Text',
		defaultTitle: 'Send a text',
		icon: MessageSquare,
		tint: 'text-sky-600 dark:text-sky-400',
		wash: 'bg-sky-500/10',
	},
	email: {
		label: 'Email',
		defaultTitle: 'Send an email',
		icon: Mail,
		tint: 'text-indigo-600 dark:text-indigo-400',
		wash: 'bg-indigo-500/10',
	},
	meeting: {
		label: 'Meeting',
		defaultTitle: 'Meeting',
		icon: CalendarDays,
		tint: 'text-amber-600 dark:text-amber-400',
		wash: 'bg-amber-500/10',
	},
	todo: {
		label: 'To-do',
		defaultTitle: 'Follow up',
		icon: CircleCheck,
		tint: 'text-muted-foreground',
		wash: 'bg-muted',
	},
};

export const ActionIcon = ({
	action,
	size = 'md',
	className,
}: {
	action: SequenceAction;
	size?: 'sm' | 'md';
	className?: string;
}) => {
	const meta = ACTION_META[action] ?? ACTION_META.todo;
	const Icon = meta.icon;
	return (
		<span
			aria-hidden
			className={cn(
				'inline-flex shrink-0 items-center justify-center rounded-full',
				size === 'sm' ? 'size-5' : 'size-7',
				meta.wash,
				className,
			)}
		>
			<Icon className={cn(size === 'sm' ? 'size-3' : 'size-3.5', meta.tint)} />
		</span>
	);
};

/** "4 steps over 30 days" */
export const planLength = (steps: { day: number }[]) => {
	if (!steps.length) return 'No steps yet';
	const lastDay = Math.max(...steps.map((step) => step.day));
	const count = `${steps.length} step${steps.length === 1 ? '' : 's'}`;
	return lastDay <= 1 ? `${count} on day 1` : `${count} over ${lastDay} days`;
};

/** 'HH:mm' → "9:00 am" in the account's locale */
export const formatClockTime = (time: string) => moment(time, 'HH:mm').format('LT');

/** "Today, 9:00 am" / "Tomorrow, 9:00 am" / "Mon 10 Nov" style due label */
export const formatStepDue = (iso: string, { withTime = true } = {}) => {
	const due = moment(iso);
	const time = withTime ? `, ${due.format('LT')}` : '';
	if (due.isSame(moment(), 'day')) return `Today${time}`;
	if (due.isSame(moment().add(1, 'day'), 'day')) return `Tomorrow${time}`;
	if (due.isSame(moment().subtract(1, 'day'), 'day')) return `Yesterday${time}`;
	return `${due.format('ddd ll')}${withTime ? `, ${due.format('LT')}` : ''}`;
};

/** "2 days overdue" */
export const overdueAge = (iso: string) => {
	const days = moment().startOf('day').diff(moment(iso).startOf('day'), 'days');
	if (days <= 0) return 'Overdue';
	return `${days} day${days === 1 ? '' : 's'} overdue`;
};

export const personName = (person?: { name?: string; username?: string } | string | null) =>
	person && typeof person === 'object' ? person.name || person.username || 'A teammate' : '';

/** "Finished 4 Nov 2026", "Stopped 4 Nov 2026 · customer replied" or "Removed 4 Nov 2026" */
export const runEndedLabel = (run: Pick<Enrollment, 'status' | 'completedAt' | 'stoppedAt' | 'stopReason'>) => {
	if (run.status === 'completed') {
		return `Finished ${run.completedAt ? moment(run.completedAt).format('ll') : ''}`.trim();
	}
	const date = run.stoppedAt ? ` ${moment(run.stoppedAt).format('ll')}` : '';
	return run.stopReason === 'customer_replied' ? `Stopped${date} · customer replied` : `Removed${date}`;
};

/** "Added automatically · card moved to Quoted"; null when someone added them */
export const runAddedAutomatically = (run: Pick<Enrollment, 'source' | 'trigger'>) =>
	run.source === 'automation'
		? run.trigger?.label
			? `Added automatically · ${run.trigger.label}`
			: 'Added automatically'
		: null;
